import moment from "moment";
import { CommissaryOrder } from "../../../../types/order-commissary";
import { CommissaryVendorGroup, formatStatus, usd } from "./helpers";

interface Props {
  group: CommissaryVendorGroup;
  expandedOrderId: number | null;
  onToggleOrder: (id: number) => void;
}

export const CommissaryVendorCard = ({
  group,
  expandedOrderId,
  onToggleOrder,
}: Props) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:border-red-500 transition-colors">
    {/* Vendor header */}
    <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
      <div>
        <div className="font-semibold text-gray-900 text-base">{group.vendorName}</div>
        <div className="text-xs text-gray-400">
          {group.orderCount} order{group.orderCount !== 1 ? "s" : ""}
          {group.pocName && (
            <>
              {" \u00b7 "}
              {group.pocName}
              {group.pocPhone && ` (${group.pocPhone})`}
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-6 text-sm">
        <Stat label="Subtotal" value={usd(group.totalSubtotal)} />
        <Stat label="Tax" value={usd(group.totalTax)} />
        <Stat label="Total" value={usd(group.totalTotal)} bold color="text-gray-900" />
      </div>
    </div>

    {/* Order rows */}
    <div className="divide-y divide-gray-100">
      {group.orders.map((order) => {
        const isExpanded = expandedOrderId === order.id;
        return (
          <div key={order.id}>
            <button
              onClick={() => onToggleOrder(order.id)}
              className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <svg
                  className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${
                    isExpanded ? "rotate-90" : ""
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="text-left min-w-0">
                  <div className="text-sm font-medium text-gray-700 truncate">
                    Order #{order.id}
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      {formatStatus(order.status)}
                    </span>
                  </div>
                  {order.user && (order.user.first_name || order.user.last_name) && (
                    <div className="text-[11px] text-gray-500">
                      <span className="text-gray-400 uppercase tracking-wide mr-1">
                        Placed by:
                      </span>
                      {`${order.user.first_name ?? ""} ${order.user.last_name ?? ""}`.trim()}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-5 text-xs flex-shrink-0">
                <Stat label="Items" value={String(order.items?.length ?? 0)} small />
                <Stat label="Subtotal" value={usd(order.subtotal)} small />
                <Stat label="Tax" value={usd(order.tax)} small />
                <Stat label="Total" value={usd(order.total)} small bold color="text-gray-900" />
              </div>
            </button>

            {isExpanded && (
              <div className="border-t-2 border-b-2 border-gray-300 bg-gray-100 border-l-4 border-l-red-700 px-5 py-4 shadow-inner">
                <OrderLineItems order={order} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

const OrderLineItems = ({ order }: { order: CommissaryOrder }) => {
  const items = Array.isArray(order.items) ? order.items : [];
  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <div className="text-sm text-gray-400 italic">No line items on this order.</div>
      ) : (
        <div className="bg-white rounded-md border border-gray-300 overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-800 text-[10px] uppercase text-gray-100">
              <tr>
                <th className="text-left px-3 py-2 font-semibold tracking-wide">Item</th>
                <th className="text-right px-3 py-2 font-semibold tracking-wide">Qty</th>
                <th className="text-right px-3 py-2 font-semibold tracking-wide">Unit Price</th>
                <th className="text-right px-3 py-2 font-semibold tracking-wide">Line Total</th>
                <th className="text-left px-3 py-2 font-semibold tracking-wide">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map((item, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="px-3 py-2 text-gray-800 font-medium">{item.item_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                    {item.quantity}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                    {usd(item.unit_price)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-900 font-semibold">
                    {usd(item.total_price)}
                  </td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{item.notes ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(order.notes || order.organizer_notes || order.rejection_reason) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          {order.notes && (
            <NoteBlock label="Vendor Notes" value={order.notes} />
          )}
          {order.organizer_notes && (
            <NoteBlock label="Organizer Notes" value={order.organizer_notes} />
          )}
          {order.rejection_reason && (
            <NoteBlock label="Rejection Reason" value={order.rejection_reason} />
          )}
        </div>
      )}

      {order.fulfilled_at && (
        <div className="text-[11px] text-gray-400">
          Fulfilled: {moment(order.fulfilled_at).format("MMM DD, YYYY h:mm a")}
        </div>
      )}
    </div>
  );
};

const NoteBlock = ({ label, value }: { label: string; value: string }) => (
  <div className="bg-white rounded-md border border-gray-200 p-2">
    <div className="text-[9px] uppercase text-gray-400 font-medium mb-0.5">{label}</div>
    <div className="text-gray-700">{value}</div>
  </div>
);

const Stat = ({
  label,
  value,
  color,
  bold,
  small,
}: {
  label: string;
  value: string;
  color?: string;
  bold?: boolean;
  small?: boolean;
}) => (
  <div className="text-right">
    <div className={`text-gray-400 uppercase ${small ? "text-[9px]" : "text-[10px]"}`}>
      {label}
    </div>
    <div
      className={`tabular-nums ${color ?? "text-gray-700"} ${bold ? "font-semibold" : ""} ${
        small ? "text-xs" : ""
      }`}
    >
      {value}
    </div>
  </div>
);
